import styled from "@emotion/styled";
import { type UserInfo } from "@repo/api";
import { LightMode, Text, crossCircleIcon, personIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  FlexDiv,
  ListViewItemWithActions,
  ListViewItemWithActionsCheckbox,
  type ListViewItemWithActionsProps,
  SearchCoworker,
  SectionContainer,
  useSettings,
} from "@repo/ui";
import { getAbbreviationIndex, maskEmail } from "@repo/utils";
import unionBy from "lodash/unionBy";
import { type ReactElement, useEffect, useState } from "react";

interface MemberListProps {
  members: UserInfo[];
  setMembers: (members: UserInfo[]) => void;
  showUnassigned?: boolean;
  editMode?: boolean;
  showSearch?: boolean;
  managerEmailId?: string | null;
  setManager?: (user: UserInfo) => void;
}

interface TeamListItem {
  value: string;
  name: string;
  title: ReactElement;
  description?: string;
  icon?: ListViewItemWithActionsProps["icon"];
}

const ClickableContainer = styled(FlexDiv)<{
  show: boolean;
  select: boolean;
  clickable: boolean;
}>`
  opacity: ${({ show, select }) => (select ? "1" : show ? "0.45" : "0")};

  ${({ clickable }) =>
    clickable
      ? `&:hover {
  text-decoration: underline;
  cursor: pointer;
  opacity: 1;
}`
      : ""}
`;

export const MemberList = ({
  members,
  setMembers,
  showUnassigned,
  editMode,
  showSearch = true,
  managerEmailId,
  setManager,
}: MemberListProps) => {
  const { customerMode } = useSettings();
  const [showTeam, setShowTeam] = useState<boolean>(true);
  const [selectedMembers, setSelectedMembers] = useState<UserInfo[]>(members);

  // Those two states are needed to display the full list before saving
  const [localMemberList, setLocalMemberList] = useState<UserInfo[]>(members);
  const [searchedMembers, setSearchedMembers] = useState<UserInfo[]>([]);
  const [localManager, setLocalManager] = useState<string | undefined | null>(
    managerEmailId
  );
  const [hoveredMember, setHoveredMember] = useState<string | undefined>();

  useEffect(() => {
    setSelectedMembers(members);
  }, [members]);

  useEffect(() => {
    setLocalMemberList(
      unionBy(members, selectedMembers, searchedMembers, "emailId")
    );
  }, [members, selectedMembers, searchedMembers, setLocalMemberList]);

  const getList = (users: UserInfo[]): TeamListItem[] => {
    const list: TeamListItem[] = users?.map((user) => ({
      value: user.emailId ?? "",
      name: user.emailId,
      title: (
        <FlexDiv justifyContent="space-between" width="100%" grow>
          <Text textStyle="Heading.XS">
            <CoWorkerName
              email={user.emailId}
              textStyle="Heading.XS"
              emails={users.map((u) => u.emailId)}
            />
          </Text>
          {editMode === false && user.emailId === managerEmailId && (
            <Text
              color={LightMode.colourStaticIkeaFamily}
              textStyle="Heading.XS"
            >
              {t("label.manager")}
            </Text>
          )}
        </FlexDiv>
      ),
      description: customerMode
        ? maskEmail(
            user.emailId,
            getAbbreviationIndex(
              user.emailId,
              members.map((m) => m.emailId)
            )
          )
        : user.emailId,
      icon: personIcon,
    }));

    if (showUnassigned) {
      list.unshift({
        value: "unassigned",
        title: <>{t("label.unassigned")}</>,
        name: t("label.unassigned"),
        icon: crossCircleIcon,
      });
    }
    return list;
  };

  const onAddMember = (users: UserInfo[]) => {
    const newMembers = users.filter(
      (u) =>
        !localMemberList.some(
          (localMember) => localMember.emailId === u.emailId
        )
    );

    setSearchedMembers([...searchedMembers, ...newMembers]);
    setSelectedMembers([...selectedMembers, ...newMembers]);
    setMembers([...selectedMembers, ...newMembers]);
  };

  const onChange = (checked: boolean, item: TeamListItem) => {
    const memberInList = localMemberList.find(
      (member) => member.emailId === item.value
    );
    let newMembers: UserInfo[];
    if (!memberInList) {
      newMembers = [];
    } else {
      newMembers = checked
        ? [...(selectedMembers ?? []), memberInList]
        : selectedMembers?.filter((el) => el.emailId !== memberInList.emailId);
    }

    setSelectedMembers(newMembers);
    setMembers(newMembers);
  };

  return (
    <SectionContainer>
      {editMode && showSearch && (
        <SearchCoworker
          key={`${searchedMembers.map((el) => el.emailId).join(",")}`}
          selectedCoworkers={selectedMembers}
          onSelectCoworker={onAddMember}
          setIsActivated={(isActivated) => setShowTeam(!isActivated)}
          canSelectMultipleCoworkers
        />
      )}
      {showTeam && (
        <div>
          {getList(localMemberList)?.map((item) => {
            const isChecked = Boolean(
              selectedMembers.find((el) => el.emailId === item.value)
            );

            return (
              <ListViewItemWithActions
                key={`member-list-${item.value}`}
                title={item.title ?? "-"}
                icon={item.icon}
                description={item.description}
                onClick={() => onChange(!isChecked, item)}
                onMouseEnter={() => setHoveredMember(item.value)}
                onMouseLeave={() => {
                  if (hoveredMember === item.value) {
                    setHoveredMember(undefined);
                  }
                }}
                action={
                  editMode ? (
                    <>
                      <ClickableContainer
                        clickable={editMode ?? false}
                        select={localManager === item.value}
                        show={hoveredMember === item.value}
                        onClick={(e) => {
                          e?.preventDefault();
                          e?.stopPropagation();
                          const selected = localMemberList.find(
                            (el) => item.value === el.emailId
                          );
                          if (editMode && selected) {
                            setLocalManager(selected.emailId);
                            setManager?.(selected);
                          }
                        }}
                      >
                        <Text
                          color={LightMode.colourStaticIkeaFamily}
                          textStyle="Heading.XS"
                        >
                          {t("label.manager")}
                        </Text>
                      </ClickableContainer>
                      <ListViewItemWithActionsCheckbox
                        label={item.name}
                        subtle
                        id={`checkbox-${item.value}`}
                        value={item.value}
                        onChange={(e) => {
                          e?.preventDefault();
                          e?.stopPropagation();
                          onChange(e.target.checked, item);
                        }}
                        checked={isChecked}
                      />
                    </>
                  ) : undefined
                }
              />
            );
          })}
        </div>
      )}
    </SectionContainer>
  );
};
